import type { AutomationType } from '@playbook-technologies/common-enums';
import {
  Alert,
  Autocomplete,
  Button,
  Collapse,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import {
  SendTestStep,
  useSendTestViewModel,
} from '../../view-model/AutomationSendTest.viewModel';
import * as styles from '../Automations.view.css';

const toStep = (raw: string): SendTestStep =>
  raw === 'all' ? 'all' : Number(raw);

export const AutomationSendTestView = ({
  creatorId,
  automationType,
}: {
  creatorId: number;
  automationType: AutomationType;
}) => {
  const vm = useSendTestViewModel(creatorId, automationType);
  const [open, setOpen] = useState(false);

  if (vm.type !== 'fetched' || !vm.value.canSendTest) return null;
  const { value } = vm;

  return (
    <div className={styles.sendTest}>
      <Button size="small" onClick={() => setOpen((current) => !current)}>
        {open ? 'Hide send test' : 'Send test'}
      </Button>
      <Collapse in={open} unmountOnExit>
        <form
          className={styles.sendTestForm}
          onSubmit={(event) => {
            event.preventDefault();
            void value.submit();
          }}
        >
          <div className={styles.filters}>
            <TextField
              size="small"
              select
              label="Step"
              className={styles.sendTestSelect}
              value={String(value.form.step)}
              onChange={(event) => value.setStep(toStep(event.target.value))}
            >
              {value.stepOptions.map((option) => (
                <MenuItem
                  key={String(option.value)}
                  value={String(option.value)}
                >
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              select
              label="Variant"
              className={styles.sendTestSelect}
              value={value.form.variant}
              onChange={(event) => value.setVariant(event.target.value)}
            >
              <MenuItem value="">All variants</MenuItem>
              {value.variants.map((variant) => (
                <MenuItem key={variant} value={variant}>
                  {variant}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="userId"
              className={styles.sendTestSelect}
              value={value.form.userId}
              error={Boolean(value.validation.userId)}
              helperText={value.validation.userId ?? 'Needed for push steps'}
              onChange={(event) => value.setUserId(event.target.value)}
            />
          </div>
          <Autocomplete
            multiple
            freeSolo
            autoSelect
            options={[]}
            value={value.form.recipients}
            onChange={(_event, recipients) => value.setRecipients(recipients)}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Recipients"
                placeholder="Type an email and press Enter"
                error={Boolean(value.validation.recipients)}
                helperText={value.validation.recipients ?? 'Up to 5 addresses'}
              />
            )}
          />
          <div>
            <Button
              type="submit"
              variant="contained"
              disabled={!value.canSubmit}
            >
              {value.sending ? 'Sending…' : 'Send'}
            </Button>
          </div>
          {value.outcome.type === 'fetch-error' ? (
            <Alert severity="error">{value.outcome.value}</Alert>
          ) : null}
          {value.outcome.type === 'fetched' ? (
            <Alert severity="success">
              {value.outcome.value.sent.map((line, index) => (
                <Typography variant="body2" key={`${index}-${line}`}>
                  {line}
                </Typography>
              ))}
              {value.outcome.value.skipped.map((line, index) => (
                <Typography
                  variant="body2"
                  className={styles.muted}
                  key={`${index}-${line}`}
                >
                  {line}
                </Typography>
              ))}
            </Alert>
          ) : null}
        </form>
      </Collapse>
    </div>
  );
};
